import { storage } from '../firebaseConfig';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { MEDIA_FILE_SIZE_LIMITS } from './mediaLimits';

const DEFAULT_CONTENT_TYPES = {
  image: 'image/jpeg',
  photo: 'image/jpeg',
  video: 'video/mp4',
  audio: 'audio/mpeg',
};

const formatMB = (bytes) => `${Math.round(bytes / 1024 / 1024)}MB`;
const IMAGE_COMPRESSION_MAX_EDGE = 2000;
const IMAGE_COMPRESSION_QUALITY = 0.82;

const shouldCompressImage = (file, contentType) => {
  if (!contentType.startsWith('image/')) return false;
  if (contentType.includes('gif') || contentType.includes('svg')) return false;
  return file.size > 650 * 1024;
};

export const emitUploadStatus = (detail) => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent('lumirae-upload-status', { detail }));
};

export const announceUploadPreparing = (type, fileName = '') => {
  const normalizedType = type === 'photo' ? 'image' : type;
  emitUploadStatus({
    active: true,
    progress: 0,
    stage: 'preparing',
    type: normalizedType,
    fileName,
  });
};

export const announceUploadFailed = (type, error, fileName = '') => {
  const normalizedType = type === 'photo' ? 'image' : type;
  emitUploadStatus({
    active: false,
    progress: 0,
    stage: 'failed',
    type: normalizedType,
    fileName,
    error: error?.message || String(error || 'Upload failed. Please try again.'),
  });
};

const compressImageFile = (file, normalizedType, contentType) => {
  if (normalizedType !== 'image' || !shouldCompressImage(file, contentType)) {
    return Promise.resolve(file);
  }

  return new Promise((resolve) => {
    const image = new Image();
    const objectUrl = URL.createObjectURL(file);

    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      const ratio = Math.min(1, IMAGE_COMPRESSION_MAX_EDGE / Math.max(image.width, image.height));
      const width = Math.max(1, Math.round(image.width * ratio));
      const height = Math.max(1, Math.round(image.height * ratio));
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext('2d');
      if (!context) {
        resolve(file);
        return;
      }
      context.drawImage(image, 0, 0, width, height);
      canvas.toBlob((blob) => {
        if (!blob || blob.size >= file.size) {
          resolve(file);
          return;
        }
        const baseName = file.name.replace(/\.[^.]+$/, '');
        resolve(new File([blob], `${baseName}.jpg`, {
          type: 'image/jpeg',
          lastModified: Date.now(),
        }));
      }, 'image/jpeg', IMAGE_COMPRESSION_QUALITY);
    };

    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(file);
    };

    image.src = objectUrl;
  });
};

const getReadableUploadError = (error, normalizedType) => {
  if (error.code === 'storage/unauthorized') {
    return 'Upload permission expired. Please save or log in again, then retry.';
  }
  if (error.code === 'storage/canceled') {
    return 'Upload was canceled before it finished.';
  }
  if (error.code === 'storage/retry-limit-exceeded') {
    return 'Network was unstable during upload. Please try again on a stronger connection.';
  }
  if (error.message) return error.message;
  return `${normalizedType} upload failed. Please try again.`;
};

export const uploadFile = async (file, type, path, onProgress) => {
  if (!file) throw new Error('No file provided');

  const normalizedType = type === 'photo' ? 'image' : type;
  const maxFileSize = MEDIA_FILE_SIZE_LIMITS[type] || MEDIA_FILE_SIZE_LIMITS[normalizedType] || 50 * 1024 * 1024;
  const originalContentType = file.type || DEFAULT_CONTENT_TYPES[type] || DEFAULT_CONTENT_TYPES[normalizedType];

  if (normalizedType === 'image' && !originalContentType.startsWith('image/')) {
    throw new Error('Please upload a supported image file.');
  }

  if (normalizedType === 'video' && !originalContentType.startsWith('video/')) {
    throw new Error('Please upload a supported video file.');
  }

  if (normalizedType === 'audio' && !originalContentType.startsWith('audio/') && originalContentType !== 'video/webm') {
    throw new Error('Please upload a supported audio file.');
  }

  let uploadFileObject = file;
  if (normalizedType === 'image') {
    emitUploadStatus({
      active: true,
      progress: 4,
      stage: 'compressing',
      type: normalizedType,
      fileName: file.name || 'image file',
    });
    uploadFileObject = await compressImageFile(file, normalizedType, originalContentType);
  }

  if (uploadFileObject.size > maxFileSize) {
    const message = `File is too large. Maximum ${normalizedType} size is ${formatMB(maxFileSize)}.`;
    emitUploadStatus({
      active: false,
      progress: 0,
      stage: 'failed',
      type: normalizedType,
      fileName: uploadFileObject.name || file.name || 'media file',
      error: message,
    });
    throw new Error(message);
  }

  const contentType = uploadFileObject.type || DEFAULT_CONTENT_TYPES[type] || DEFAULT_CONTENT_TYPES[normalizedType];
  const storageRef = ref(storage, path);
  const metadata = contentType ? { contentType } : undefined;
  const uploadTask = uploadBytesResumable(storageRef, uploadFileObject, metadata);
  emitUploadStatus({
    active: true,
    progress: normalizedType === 'image' && uploadFileObject !== file ? 8 : 0,
    stage: 'uploading',
    type: normalizedType,
    fileName: uploadFileObject.name || file.name || 'media file',
  });

  return new Promise((resolve, reject) => {
    uploadTask.on(
      'state_changed',
      (snapshot) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        if (onProgress) onProgress(progress);
        emitUploadStatus({
          active: true,
          progress,
          stage: 'uploading',
          type: normalizedType,
          fileName: uploadFileObject.name || file.name || 'media file',
        });
      },
      (error) => {
        console.error('Upload error:', error);
        const message = getReadableUploadError(error, normalizedType);
        emitUploadStatus({
          active: false,
          progress: 0,
          stage: 'failed',
          type: normalizedType,
          error: message,
        });
        reject(new Error(message));
      },
      async () => {
        try {
          emitUploadStatus({
            active: true,
            progress: 100,
            stage: 'processing',
            type: normalizedType,
            fileName: uploadFileObject.name || file.name || 'media file',
          });
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          emitUploadStatus({
            active: false,
            progress: 100,
            stage: 'ready',
            type: normalizedType,
            fileName: uploadFileObject.name || file.name || 'media file',
          });
          resolve(downloadURL);
        } catch (err) {
          emitUploadStatus({
            active: false,
            progress: 0,
            stage: 'failed',
            type: normalizedType,
            error: err.message,
          });
          reject(new Error(`Failed to get download URL: ${err.message}`));
        }
      }
    );
  });
};

export const deleteFile = async (url) => {
  // Deprecated: frontend deletes are blocked by storage.rules. Use a trusted Cloud Function instead.
  if (!url || !url.includes('firebasestorage.googleapis.com')) return;
  try {
    const decodedUrl = decodeURIComponent(url);
    const path = decodedUrl.split('/o/')[1].split('?')[0];
    const fileRef = ref(storage, path);
    await deleteObject(fileRef);
  } catch (error) {
    console.error('Delete error:', error);
  }
};
